import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Layout from '../components/Layout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const fmt = (n: number) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  suspended: { label: 'Geschorst', cls: 'bg-amber-100 text-amber-700' },
  blocked: { label: 'Geblokkeerd', cls: 'bg-red-100 text-red-700' },
  inactive: { label: 'Wacht op goedkeuring', cls: 'bg-slate-100 text-slate-600' },
};
const statusBadge = (status?: string) => {
  const s = status && STATUS_BADGE[status];
  return s ? <span className={`ml-2 align-middle text-[10px] font-medium px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span> : null;
};

export const AdminMembersPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) { navigate('/login'); return; }
    fetch(`${API_BASE_URL}/api/admin/collectivities/${id}/members`, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((r) => (r.ok ? r.json() : [])).then(setMembers).finally(() => setLoading(false));
  }, [isAuthenticated, accessToken, navigate, id]);

  const verbruik = (used: number, max: number) => (
    <span className={used > max ? 'text-mfb-orange font-semibold' : 'text-slate-700'}>{used} / {max}</span>
  );

  return (
    <Layout>
      <button onClick={() => navigate('/backoffice/collectivities')} className="text-sm text-mfb-blue hover:underline mb-4">← Alle collectiviteiten</button>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-semibold text-mfb-blue">Leden</h1>
          <p className="text-sm text-slate-500">Klik op een lid voor het volledige overzicht.</p>
        </div>
        <button onClick={() => setShowInvite(true)}
          className="rounded-lg bg-mfb-orange px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600">
          + Lid uitnodigen
        </button>
      </div>
      {showInvite && <InviteForm cid={id!} token={accessToken!} onClose={() => setShowInvite(false)} />}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Laden...</div>
      ) : members.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">Nog geen leden in deze collectiviteit.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50"><tr>
              {['Lid', 'Opinies', "Incasso's", 'Openstaand', 'Verzekerd', 'Premie', 'Servicefee'].map((h) => (
                <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>
              ))}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {members.map((m) => (
                <tr key={m.id} onClick={() => navigate(`/backoffice/members/${m.id}`)} className="hover:bg-slate-50 cursor-pointer">
                  <td className="px-5 py-3 font-medium text-mfb-blue">{m.company_name}<span className="text-mfb-orange ml-2">→</span>{statusBadge(m.status)}<div className="text-xs text-slate-400">{m.full_name}</div></td>
                  <td className="px-5 py-3">{verbruik(m.opinies_gebruikt, m.inbegrepen_opinies)}</td>
                  <td className="px-5 py-3">{verbruik(m.incassos_gebruikt, m.inbegrepen_incassos)}</td>
                  <td className="px-5 py-3 text-slate-700">{fmt(m.openstaand)}</td>
                  <td className="px-5 py-3 text-slate-700">{fmt(m.verzekerd)}</td>
                  <td className="px-5 py-3 text-slate-700">{fmt(m.premie)}</td>
                  <td className="px-5 py-3 text-slate-700">{fmt(m.servicefee)}{m.bijbetaling > 0 && <span className="text-xs text-mfb-orange"> (+{fmt(m.bijbetaling)})</span>}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}
    </Layout>
  );
};

export const InviteForm: React.FC<{ cid: string; token: string; onClose: () => void }> = ({ cid, token, onClose }) => {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [company, setCompany] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ link: string; emailed: boolean } | null>(null);
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    setError(''); setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/invites`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ collectivity_id: cid, email: email.trim(), full_name: fullName.trim() || undefined, company_name: company.trim() || undefined }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Uitnodigen mislukt'); }
      const d = await res.json();
      setResult({ link: d.link, emailed: d.emailed });
    } catch (e: any) { setError(e.message || 'Uitnodigen mislukt'); }
    finally { setSaving(false); }
  };

  const copy = () => { if (result) { navigator.clipboard.writeText(result.link); setCopied(true); setTimeout(() => setCopied(false), 2000); } };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-mfb-blue mb-1">Lid uitnodigen</h2>
        <p className="text-sm text-slate-500 mb-5">Het lid krijgt een onboarding-link en stelt zelf een wachtwoord in.</p>

        {!result ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">E-mailadres <span className="text-mfb-orange">*</span></label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} autoFocus type="email" placeholder="naam@bedrijf.nl"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-mfb-orange focus:outline-none focus:ring-1 focus:ring-mfb-orange" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Naam</label>
                <input value={fullName} onChange={(e) => setFullName(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-mfb-orange focus:outline-none focus:ring-1 focus:ring-mfb-orange" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Bedrijf</label>
                <input value={company} onChange={(e) => setCompany(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-mfb-orange focus:outline-none focus:ring-1 focus:ring-mfb-orange" />
              </div>
            </div>
            {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">Annuleren</button>
              <button onClick={submit} disabled={saving || !email.includes('@')}
                className="rounded-lg bg-mfb-orange px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-50">
                {saving ? 'Versturen…' : 'Uitnodiging versturen'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className={`rounded-lg px-3 py-2 text-sm ${result.emailed ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
              {result.emailed
                ? 'Uitnodiging per e-mail verstuurd.'
                : 'Geen mailserver ingesteld — deel onderstaande link handmatig met het lid.'}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Onboarding-link</label>
              <div className="flex gap-2">
                <input readOnly value={result.link} className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm bg-slate-50" />
                <button onClick={copy} className="rounded-lg bg-mfb-blue px-3 py-2 text-sm font-semibold text-white hover:bg-mfb-blue-dark whitespace-nowrap">
                  {copied ? 'Gekopieerd' : 'Kopiëren'}
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <button onClick={onClose} className="rounded-lg bg-mfb-orange px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600">Klaar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMembersPage;
